import { DEV_DEFAULTS } from '../config/devDefaults';
import type { SessionContext } from './session.types';

/*
 * Token & session store.
 *
 * Storage rules (per CRS-Mobile-Developer-Guide.md §5.2, adapted for the
 * current BFF template which does NOT yet issue rotating refresh tokens):
 *
 *   Access token     → in-memory + sessionStorage (dev compromise, see NOTE)
 *   Session context  → sessionStorage             (dev compromise, see NOTE)
 *   Refresh token    → localStorage (survives tab close — for when BFF issues one)
 *   Property ID      → localStorage
 *   Tenant / App ID  → localStorage (with DEV fallback)
 *
 * NOTE — access token + session persistence
 * The spec says access token must be memory-only. That plus a proper refresh
 * token would give F5-safe reloads without persisting the JWT. Until the BFF
 * template issues refresh tokens, we cache the access token in sessionStorage
 * so an F5 doesn't force a re-login. sessionStorage (vs localStorage) at least
 * clears on tab close and is scoped per-tab, so an XSS attacker's window is
 * narrower than persistent storage.
 *
 * When the BFF starts issuing refresh tokens, drop the sessionStorage backing
 * for the access token: change `getAccessToken` to only read `accessToken`
 * (memory) and remove the two lines that write to `sessionStorage`.
 */

const REFRESH_KEY = 'crs.mob.refresh';
const PROPERTY_KEY = 'crs.mob.propertyId';
const TENANT_KEY = 'crs.mob.tenantId';
const APP_KEY = 'crs.mob.appId';
const ACCESS_KEY = 'crs.mob.access';    // sessionStorage
const SESSION_KEY = 'crs.mob.session';  // sessionStorage

let accessToken: string | null = null;

function readLs(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLs(key: string, value: string | null): void {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch {
    // Storage unavailable (private mode etc.) — swallow.
  }
}

function readSs(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSs(key: string, value: string | null): void {
  try {
    if (value) sessionStorage.setItem(key, value);
    else sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
}

export const tokenStore = {
  // ---- Access token ----------------------------------------------------
  /**
   * Reads the in-memory access token; if empty, warms from sessionStorage
   * (surviving F5) and returns that. Returns null when neither has a value.
   */
  getAccessToken(): string | null {
    if (accessToken) return accessToken;
    const persisted = readSs(ACCESS_KEY);
    if (persisted) accessToken = persisted;
    return accessToken;
  },
  setAccessToken(token: string | null): void {
    accessToken = token;
    writeSs(ACCESS_KEY, token);
  },

  // ---- Refresh token ---------------------------------------------------
  getRefreshToken(): string | null {
    return readLs(REFRESH_KEY);
  },
  setRefreshToken(token: string | null): void {
    writeLs(REFRESH_KEY, token);
  },

  // ---- Session context (F5-restore) ------------------------------------
  getPersistedSession(): SessionContext | null {
    const raw = readSs(SESSION_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as SessionContext;
    } catch {
      return null;
    }
  },
  setPersistedSession(session: SessionContext | null): void {
    writeSs(SESSION_KEY, session ? JSON.stringify(session) : null);
  },

  // ---- Property / tenant / app context ---------------------------------
  getSelectedPropertyId(): string | null {
    return readLs(PROPERTY_KEY);
  },
  setSelectedPropertyId(id: string | null): void {
    writeLs(PROPERTY_KEY, id);
  },

  getTenantId(): string | null {
    return readLs(TENANT_KEY) ?? DEV_DEFAULTS.tenantId;
  },
  setTenantId(id: string | null): void {
    writeLs(TENANT_KEY, id);
  },

  getAppId(): string | null {
    return readLs(APP_KEY) ?? DEV_DEFAULTS.appId;
  },
  setAppId(id: string | null): void {
    writeLs(APP_KEY, id);
  },

  // ---- Reset -----------------------------------------------------------
  /**
   * Clear all auth artifacts. Property / tenant / app context are preserved —
   * they are user preferences, not auth tokens.
   */
  clear(): void {
    accessToken = null;
    writeSs(ACCESS_KEY, null);
    writeSs(SESSION_KEY, null);
    writeLs(REFRESH_KEY, null);
  },
};
